/**
 * Contratos por mes — reemplazo de `contrato-anual` y `contrato-mensual` del
 * sistema anterior de marva (2026-10-22).
 *
 * Un contrato cuenta en un mes si su vigencia toca ese mes, y su valor pactado
 * entra completo en cada uno (como en marva, sin prorrateo). La regla de marva
 * dejaba afuera del mes de inicio a un contrato que empezaba a mitad de mes y
 * duraba más; esta no.
 *
 * El backend responde el año entero de una vez: la fila de meses muestra cuántos
 * contratos y cuánto suma cada uno, y elegir un mes solo filtra en el cliente.
 * Los filtros por campo personalizado salen de los contratos mismos: hoy es la
 * emisora, mañana puede ser otro campo, sin programar nada.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import * as XLSX from 'xlsx'

import apiClient from '@/lib/apiClient'
import { formatMoneyForTenant } from '@/lib/locale'
import { fmtDate, todayISO } from '@/lib/utils'
import { DataTable, type DataTableColumn } from '@/components/ds/DataTable'
import { SelectField } from '@/components/ds/SelectField'
import { EmptyState } from '@/components/EmptyState'

interface MesResumen { month: number; count: number; total: string; other_currency_count: number }
interface CampoPersonalizado { name: string; values: string[] | null }
interface ContratoFila {
  id: string
  contract_number: string
  customer: string
  title: string
  value: string
  currency: string
  start_date: string
  end_date: string | null
  status: string
  seller: string
  custom_fields: Record<string, string>
  months: number[]
}
interface Respuesta {
  year: number
  currency: string
  months: MesResumen[]
  contracts: ContratoFila[]
  filters: { custom_fields: CampoPersonalizado[]; sales_reps: { id: string; name: string }[] }
}

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre',
  'Octubre', 'Noviembre', 'Diciembre']
const ESTADOS: Record<string, string> = { active: 'Vigente', expired: 'Vencido', renewed: 'Renovado' }

const hoy = new Date()

function useContratosPorMes(year: number, salesRep: string, campos: Record<string, string>) {
  return useQuery({
    queryKey: ['reports', 'contracts-by-month', year, salesRep, campos],
    queryFn: async () => {
      const params: Record<string, string> = { year: String(year) }
      if (salesRep) params.sales_rep = salesRep
      for (const [nombre, valor] of Object.entries(campos)) if (valor) params[`cf_${nombre}`] = valor
      const res = await apiClient.get('/reports/contracts-by-month/', { params })
      return res.data as Respuesta
    },
  })
}

export function ContractsByMonthReport() {
  const [year, setYear] = useState(hoy.getFullYear())
  const [mes, setMes] = useState(hoy.getMonth() + 1)
  const [salesRep, setSalesRep] = useState('')
  const [campos, setCampos] = useState<Record<string, string>>({})
  // Lo que se escribe en un campo de texto se aplica al salir del campo, no en
  // cada tecla: cada cambio vuelve a pedir el año entero.
  const [borrador, setBorrador] = useState<Record<string, string>>({})
  const { data, isLoading, error } = useContratosPorMes(year, salesRep, campos)

  const nombresCampos = data?.filters.custom_fields.map(c => c.name) ?? []
  const delMes = useMemo(
    () => (data?.contracts ?? []).filter(c => c.months.includes(mes)),
    [data, mes],
  )
  const resumen = data?.months[mes - 1]
  const anios = Array.from({ length: 8 }, (_, i) => hoy.getFullYear() + 1 - i)
  const aplicarCampo = (nombre: string, valor: string) => setCampos(c => ({ ...c, [nombre]: valor }))

  const columnas: DataTableColumn<ContratoFila>[] = [
    { key: 'contract_number', header: 'Contrato', sortable: true,
      render: c => (
        <Link to={`/sales/contracts?search=${encodeURIComponent(c.contract_number)}`}
          className="text-primary hover:underline">{c.contract_number}</Link>
      ) },
    { key: 'customer', header: 'Cliente', sortable: true },
    { key: 'title', header: 'Campaña', sortable: true },
    { key: 'value', header: 'Monto', numeric: true, sortable: true, sortValue: c => Number(c.value),
      render: c => (c.currency === data?.currency ? formatMoneyForTenant(c.value) : `${c.currency} ${c.value}`) },
    { key: 'start_date', header: 'Inicio', sortable: true, sortValue: c => c.start_date, render: c => fmtDate(c.start_date) },
    { key: 'end_date', header: 'Término', sortable: true, sortValue: c => c.end_date ?? '9999',
      render: c => (c.end_date ? fmtDate(c.end_date) : 'Sin término') },
    ...nombresCampos.map((nombre): DataTableColumn<ContratoFila> => ({
      key: `cf_${nombre}`, header: nombre, sortable: true,
      sortValue: c => c.custom_fields[nombre] ?? '',
      render: c => c.custom_fields[nombre] ?? '',
    })),
    { key: 'seller', header: 'Vendedor', sortable: true },
    { key: 'status', header: 'Estado', render: c => ESTADOS[c.status] ?? c.status },
  ]

  const exportar = () => {
    if (!data) return
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.months.map(m => ({
      'Mes': MESES[m.month - 1], 'Contratos': m.count, 'Total': Number(m.total),
      ...(m.other_currency_count ? { 'En otra moneda (no suman)': m.other_currency_count } : {}),
    }))), 'Resumen')
    // Una fila por contrato y mes: así una tabla dinámica en Excel suma por mes.
    const detalle = data.contracts.flatMap(c => c.months.map(m => ({
      'Mes': MESES[m - 1], 'Contrato': c.contract_number, 'Cliente': c.customer, 'Campaña': c.title,
      'Monto': Number(c.value), 'Moneda': c.currency, 'Inicio': c.start_date, 'Término': c.end_date ?? '',
      ...Object.fromEntries(nombresCampos.map(n => [n, c.custom_fields[n] ?? ''])),
      'Vendedor': c.seller, 'Estado': ESTADOS[c.status] ?? c.status,
    })))
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detalle), 'Detalle')
    XLSX.writeFile(wb, `contratos_por_mes_${data.year}_${todayISO()}.xlsx`)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <SelectField label="Año" value={String(year)} onChange={e => setYear(Number(e.target.value))}
          options={anios.map(a => ({ value: String(a), label: String(a) }))} />
        <SelectField label="Vendedor" value={salesRep} onChange={e => setSalesRep(e.target.value)}
          options={[{ value: '', label: 'Todos' }, ...(data?.filters.sales_reps ?? []).map(r => ({ value: r.id, label: r.name }))]} />
        {(data?.filters.custom_fields ?? []).map(campo => campo.values ? (
          <SelectField key={campo.name} label={campo.name} value={campos[campo.name] ?? ''}
            onChange={e => aplicarCampo(campo.name, e.target.value)}
            options={[{ value: '', label: 'Todos' }, ...campo.values.map(v => ({ value: v, label: v }))]} />
        ) : (
          <div key={campo.name}>
            <label htmlFor={`cpm-${campo.name}`} className="block text-xs font-medium text-content-muted mb-1">
              {campo.name}
            </label>
            <input id={`cpm-${campo.name}`} value={borrador[campo.name] ?? campos[campo.name] ?? ''}
              onChange={e => setBorrador(b => ({ ...b, [campo.name]: e.target.value }))}
              onBlur={e => aplicarCampo(campo.name, e.target.value.trim())}
              onKeyDown={e => { if (e.key === 'Enter') aplicarCampo(campo.name, (e.target as HTMLInputElement).value.trim()) }}
              placeholder="Contiene…"
              className="px-3 py-1.5 text-sm rounded-lg border border-divider bg-surface text-content focus:ring-2 focus:ring-focus-ring" />
          </div>
        ))}
        <button onClick={exportar} disabled={!data?.contracts.length}
          className="ml-auto flex items-center gap-2 px-4 py-2 bg-success-fill text-white rounded-lg text-sm hover:bg-success-fill-hover transition-colors disabled:opacity-40">
          <Download size={15} /> Exportar Excel
        </button>
      </div>

      {error ? <p className="text-sm text-danger-content">No se pudo cargar el reporte.</p> : null}

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-12 gap-2" role="tablist" aria-label="Mes">
        {(data?.months ?? MESES.map((_, i) => ({ month: i + 1, count: 0, total: '0', other_currency_count: 0 }))).map(m => {
          const activo = m.month === mes
          return (
            <button key={m.month} role="tab" aria-selected={activo} onClick={() => setMes(m.month)}
              className={`rounded-lg border px-2 py-2 text-left transition-colors ${
                activo ? 'border-primary bg-primary-50 dark:bg-primary-950/30' : 'border-divider hover:bg-soft'}`}>
              <span className="block text-xs font-medium text-content">{MESES[m.month - 1].slice(0, 3)}</span>
              <span className="block text-[11px] text-content-muted">{isLoading ? '…' : `${m.count} contr.`}</span>
              <span className="block text-[11px] font-mono tabular-nums text-content-subtle truncate">
                {isLoading ? '' : formatMoneyForTenant(m.total)}
              </span>
            </button>
          )
        })}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-content mb-2">
          Contratos vigentes en {MESES[mes - 1].toLowerCase()} de {year}
          {resumen?.other_currency_count
            ? <span className="ml-2 text-xs font-normal text-content-muted">
                ({resumen.other_currency_count} en otra moneda, fuera del total)
              </span>
            : null}
        </h3>
        {isLoading ? <p className="text-sm text-content-muted">Cargando…</p>
          : delMes.length === 0 ? (
            <EmptyState title="Sin contratos vigentes en este mes" size="sm"
              hint="Un contrato cuenta en un mes si su vigencia toca ese mes. Probar con otro mes, año o filtro." />
          ) : (
            <DataTable columns={columnas} rows={delMes} rowKey={c => c.id}
              footer={{ value: <span className="font-semibold">{formatMoneyForTenant(resumen?.total ?? '0')}</span> }}
              footerLabel={`Total del mes · ${delMes.length} contratos`} />
          )}
      </div>
    </div>
  )
}
